import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Alert,
    Stack,
    useTheme,
    alpha,
    Tooltip,
    IconButton,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    InputAdornment,
    Button,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    CloudQueue as CloudIcon,
    Search as SearchIcon,
    CheckCircle as SuccessIcon,
    HelpOutline as NotFoundIcon,
    Refresh as RefreshIcon,
    TrendingUp as TrendingIcon,
    Apps as AppsIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────

export interface CloudWord {
    text: string;
    value: number;
    percentage: number;
    matched_platform: string | null;
    dominant_outcome: 'not_found' | 'success' | 'already_enabled' | 'not_allowed';
    outcomes: {
        not_found: number;
        success: number;
        already_enabled: number;
        not_allowed: number;
    };
    last_requested_at: string | null;
}

export interface RequestLogEntry {
    id: number;
    user_id: number;
    username: string;
    phone_number: string | null;
    raw_query: string;
    normalized_query: string;
    matched_platform: string | null;
    outcome: string;
    created_at: string;
}

export interface CloudSummary {
    total_requests: number;
    unique_queries: number;
    not_found_count: number;
    not_found_rate: number;
    success_count: number;
    already_enabled_count: number;
    not_allowed_count: number;
    outcomes: {
        not_found: number;
        success: number;
        already_enabled: number;
        not_allowed: number;
    };
    platform_breakdown: Array<{
        matched_platform: string;
        count: number;
    }>;
}

export interface CloudResponse {
    summary: CloudSummary;
    words: CloudWord[];
    recent_logs: RequestLogEntry[];
    filters: {
        date_from: string | null;
        date_to: string | null;
        outcome: string;
        query_type: string;
        limit: number;
    };
}

// ── Outcome Badges & Colors ───────────────────────────────────────────────

const OUTCOME_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
    not_found: {
        label: 'Not Found (Unmet Demand)',
        color: '#E53935',
        bg: 'rgba(229, 57, 53, 0.12)',
    },
    success: {
        label: 'Success',
        color: '#43A047',
        bg: 'rgba(67, 160, 71, 0.12)',
    },
    already_enabled: {
        label: 'Already Enabled',
        color: '#8E24AA',
        bg: 'rgba(142, 36, 170, 0.12)',
    },
    not_allowed: {
        label: 'Not Allowed',
        color: '#FB8C00',
        bg: 'rgba(251, 140, 0, 0.12)',
    },
};

// ── KPI Summary Card ──────────────────────────────────────────────────────

const KPICard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
}> = ({ title, value, subtitle, icon, color }) => {
    return (
        <Card
            sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
                border: `1px solid ${alpha(color, 0.16)}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 18px ${alpha(color, 0.14)}`,
                },
            }}
        >
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" mb={1}>
                    <Box
                        sx={{
                            backgroundColor: alpha(color, 0.14),
                            borderRadius: '10px',
                            p: 0.8,
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {title}
                    </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color={color}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

// ── Word Cloud Component ──────────────────────────────────────────────────

const WordCloudView: React.FC<{
    words: CloudWord[];
    selectedWord: string | null;
    onSelectWord: (word: string | null) => void;
}> = ({ words, selectedWord, onSelectWord }) => {
    const theme = useTheme();

    if (words.length === 0) {
        return (
            <Box py={6} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                    No search query records found for this filter.
                </Typography>
            </Box>
        );
    }

    const maxVal = Math.max(...words.map((w) => w.value), 1);
    const minVal = Math.min(...words.map((w) => w.value), 1);

    return (
        <Box
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${theme.palette.divider}`,
                minHeight: 280,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 1, sm: 1.5, md: 2 },
            }}
        >
            {words.map((word) => {
                // Calculate size scale from 13px to 38px
                const ratio = maxVal > minVal ? (word.value - minVal) / (maxVal - minVal) : 0.5;
                const fontSize = 13 + ratio * 25;
                const fontWeight = ratio > 0.4 ? 700 : ratio > 0.15 ? 600 : 500;

                const cfg = OUTCOME_CONFIG[word.dominant_outcome] || OUTCOME_CONFIG.not_found;
                const isSelected = selectedWord === word.text;

                return (
                    <Tooltip
                        key={word.text}
                        arrow
                        title={
                            <Box sx={{ p: 0.5 }}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    "{word.text}"
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Searches: <b>{word.value.toLocaleString()}</b> ({word.percentage}% of total)
                                </Typography>
                                {word.matched_platform && (
                                    <Typography variant="body2">
                                        Matched: <b>{word.matched_platform.toUpperCase()}</b>
                                    </Typography>
                                )}
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="error" display="block">
                                        • Not found: {word.outcomes.not_found}
                                    </Typography>
                                    <Typography variant="caption" color="success.main" display="block">
                                        • Success: {word.outcomes.success}
                                    </Typography>
                                    <Typography variant="caption" color="secondary" display="block">
                                        • Already enabled: {word.outcomes.already_enabled}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Click to filter detailed logs
                                </Typography>
                            </Box>
                        }
                    >
                        <Box
                            component="span"
                            onClick={() => onSelectWord(isSelected ? null : word.text)}
                            sx={{
                                fontSize: `${fontSize}px`,
                                fontWeight,
                                color: cfg.color,
                                cursor: 'pointer',
                                px: 1.4,
                                py: 0.5,
                                borderRadius: '16px',
                                backgroundColor: isSelected
                                    ? alpha(cfg.color, 0.28)
                                    : alpha(cfg.color, 0.08),
                                border: isSelected
                                    ? `2px solid ${cfg.color}`
                                    : `1px solid ${alpha(cfg.color, 0.18)}`,
                                transition: 'all 0.18s ease-in-out',
                                userSelect: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.6,
                                '&:hover': {
                                    backgroundColor: alpha(cfg.color, 0.22),
                                    transform: 'scale(1.08)',
                                    boxShadow: `0 4px 12px ${alpha(cfg.color, 0.25)}`,
                                },
                            }}
                        >
                            <span>{word.text}</span>
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '0.7em',
                                    opacity: 0.75,
                                    fontWeight: 700,
                                }}
                            >
                                {word.value}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};

// ── Main Dashboard Tab Component ──────────────────────────────────────────

const RequestWordCloud: React.FC = () => {
    const theme = useTheme();

    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [outcome, setOutcome] = useState<string>('all');
    const [queryType, setQueryType] = useState<string>('normalized');
    const [limit, setLimit] = useState<number>(100);

    const [data, setData] = useState<CloudResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Drill-down selection & table search
    const [selectedWord, setSelectedWord] = useState<string | null>(null);
    const [tableSearch, setTableSearch] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: Record<string, string | number> = {
                outcome,
                query_type: queryType,
                limit,
            };
            if (dateFrom) {
                params.date_from = dateFrom.toISOString().split('T')[0];
            }
            if (dateTo) {
                params.date_to = dateTo.toISOString().split('T')[0];
            }

            const response = await api.get<CloudResponse>(
                '/user/private-platform-requests/cloud/',
                { params }
            );
            setData(response.data);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to fetch cloud data');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, outcome, queryType, limit]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Filter words in table based on search or selectedWord
    const filteredWords = useMemo(() => {
        if (!data?.words) return [];
        let list = data.words;
        if (selectedWord) {
            list = list.filter((w) => w.text.toLowerCase() === selectedWord.toLowerCase());
        } else if (tableSearch.trim()) {
            const q = tableSearch.toLowerCase().trim();
            list = list.filter((w) => w.text.toLowerCase().includes(q));
        }
        return list;
    }, [data?.words, selectedWord, tableSearch]);

    // Quick range presets
    const handleSetDaysBack = (days: number | null) => {
        if (days === null) {
            setDateFrom(null);
            setDateTo(null);
            return;
        }
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - days);
        setDateFrom(from);
        setDateTo(to);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Header info */}
                <Box mb={2.5}>
                    <Typography variant="h5" fontWeight={700} gutterBottom display="flex" alignItems="center" gap={1}>
                        <CloudIcon color="primary" /> Private Platform Request Cloud
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Real-time intelligence on what private fantasy platforms and keywords users are searching for.
                        Highlights unmet demand (<b>not found</b>) to guide future platform support.
                    </Typography>
                </Box>

                {/* KPI Summary Cards */}
                {data?.summary && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total Inquiries"
                                value={data.summary.total_requests}
                                subtitle={`${data.summary.unique_queries} distinct terms analyzed`}
                                icon={<TrendingIcon sx={{ color: '#1976D2' }} />}
                                color="#1976D2"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Unmet Demand (Not Found)"
                                value={`${data.summary.not_found_rate}%`}
                                subtitle={`${data.summary.not_found_count.toLocaleString()} searches for unsupported apps`}
                                icon={<NotFoundIcon sx={{ color: '#E53935' }} />}
                                color="#E53935"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Successful Unlocks"
                                value={data.summary.success_count}
                                subtitle={`${data.summary.already_enabled_count} already active requests`}
                                icon={<SuccessIcon sx={{ color: '#43A047' }} />}
                                color="#43A047"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Top Platform Matches"
                                value={data.summary.platform_breakdown.map((p) => `${p.matched_platform.toUpperCase()}: ${p.count}`).join(' | ') || 'None'}
                                subtitle="PRVC / PRVD / PRVF breakdown"
                                icon={<AppsIcon sx={{ color: '#8E24AA' }} />}
                                color="#8E24AA"
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Filter Control Bar */}
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" justifyContent="space-between">
                            {/* Preset Buttons & Date Pickers */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                <Button
                                    size="small"
                                    variant={dateFrom === null && dateTo === null ? 'contained' : 'outlined'}
                                    onClick={() => handleSetDaysBack(null)}
                                >
                                    All Time
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSetDaysBack(7)}
                                >
                                    Last 7d
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSetDaysBack(30)}
                                >
                                    Last 30d
                                </Button>
                                <DatePicker
                                    label="From"
                                    value={dateFrom}
                                    onChange={setDateFrom}
                                    slotProps={{
                                        textField: { size: 'small', sx: { width: 155 } },
                                    }}
                                />
                                <DatePicker
                                    label="To"
                                    value={dateTo}
                                    onChange={setDateTo}
                                    slotProps={{
                                        textField: { size: 'small', sx: { width: 155 } },
                                    }}
                                />
                            </Stack>

                            {/* Query Type & Limit */}
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                <ToggleButtonGroup
                                    value={queryType}
                                    exclusive
                                    onChange={(_, val) => val && setQueryType(val)}
                                    size="small"
                                >
                                    <ToggleButton value="normalized">Normalized</ToggleButton>
                                    <ToggleButton value="raw">Raw</ToggleButton>
                                </ToggleButtonGroup>

                                <ToggleButtonGroup
                                    value={limit}
                                    exclusive
                                    onChange={(_, val) => val && setLimit(val)}
                                    size="small"
                                >
                                    <ToggleButton value={50}>Top 50</ToggleButton>
                                    <ToggleButton value={100}>Top 100</ToggleButton>
                                    <ToggleButton value={200}>Top 200</ToggleButton>
                                </ToggleButtonGroup>

                                <Tooltip title="Refresh data">
                                    <IconButton onClick={fetchData} color="primary" size="small">
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>

                        {/* Outcome filter chips */}
                        <Box display="flex" alignItems="center" gap={1} mt={2} flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary" fontWeight={600} mr={0.5}>
                                Filter by Outcome:
                            </Typography>
                            <Chip
                                label="All Outcomes"
                                size="small"
                                clickable
                                color={outcome === 'all' ? 'primary' : 'default'}
                                variant={outcome === 'all' ? 'filled' : 'outlined'}
                                onClick={() => setOutcome('all')}
                            />
                            <Chip
                                label="Not Found (Demand)"
                                size="small"
                                clickable
                                sx={{
                                    backgroundColor: outcome === 'not_found' ? OUTCOME_CONFIG.not_found.bg : 'transparent',
                                    color: OUTCOME_CONFIG.not_found.color,
                                    borderColor: OUTCOME_CONFIG.not_found.color,
                                    fontWeight: outcome === 'not_found' ? 700 : 500,
                                }}
                                variant="outlined"
                                onClick={() => setOutcome(outcome === 'not_found' ? 'all' : 'not_found')}
                            />
                            <Chip
                                label="Success"
                                size="small"
                                clickable
                                sx={{
                                    backgroundColor: outcome === 'success' ? OUTCOME_CONFIG.success.bg : 'transparent',
                                    color: OUTCOME_CONFIG.success.color,
                                    borderColor: OUTCOME_CONFIG.success.color,
                                    fontWeight: outcome === 'success' ? 700 : 500,
                                }}
                                variant="outlined"
                                onClick={() => setOutcome(outcome === 'success' ? 'all' : 'success')}
                            />
                            <Chip
                                label="Already Enabled"
                                size="small"
                                clickable
                                sx={{
                                    backgroundColor: outcome === 'already_enabled' ? OUTCOME_CONFIG.already_enabled.bg : 'transparent',
                                    color: OUTCOME_CONFIG.already_enabled.color,
                                    borderColor: OUTCOME_CONFIG.already_enabled.color,
                                    fontWeight: outcome === 'already_enabled' ? 700 : 500,
                                }}
                                variant="outlined"
                                onClick={() => setOutcome(outcome === 'already_enabled' ? 'all' : 'already_enabled')}
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Loading / Error states */}
                {loading && (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Word Cloud View */}
                {data && !loading && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                                <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={1}>
                                    <CloudIcon fontSize="small" color="primary" /> Interactive Term Cloud
                                </Typography>

                                {/* Legend */}
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    <Chip
                                        size="small"
                                        label="🔴 Not Found (Demand)"
                                        sx={{
                                            backgroundColor: OUTCOME_CONFIG.not_found.bg,
                                            color: OUTCOME_CONFIG.not_found.color,
                                            fontWeight: 600,
                                            fontSize: '0.72rem',
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label="🟢 Success"
                                        sx={{
                                            backgroundColor: OUTCOME_CONFIG.success.bg,
                                            color: OUTCOME_CONFIG.success.color,
                                            fontWeight: 600,
                                            fontSize: '0.72rem',
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label="🟣 Already Enabled"
                                        sx={{
                                            backgroundColor: OUTCOME_CONFIG.already_enabled.bg,
                                            color: OUTCOME_CONFIG.already_enabled.color,
                                            fontWeight: 600,
                                            fontSize: '0.72rem',
                                        }}
                                    />
                                </Stack>
                            </Box>

                            {selectedWord && (
                                <Box mb={1.5} display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" color="text.secondary">
                                        Filtering by word:
                                    </Typography>
                                    <Chip
                                        label={selectedWord}
                                        onDelete={() => setSelectedWord(null)}
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                            )}

                            <WordCloudView
                                words={data.words}
                                selectedWord={selectedWord}
                                onSelectWord={setSelectedWord}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Detailed Word Table & Recent Stream */}
                {data && !loading && (
                    <Grid container spacing={3}>
                        {/* Search Term Breakdown Table */}
                        <Grid item xs={12} md={8}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                                        <Typography variant="h6" fontWeight={600}>
                                            Keyword Frequency Breakdown ({filteredWords.length})
                                        </Typography>
                                        <TextField
                                            size="small"
                                            placeholder="Search keywords..."
                                            value={tableSearch}
                                            onChange={(e) => {
                                                setTableSearch(e.target.value);
                                                setSelectedWord(null);
                                                setPage(0);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: tableSearch ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setTableSearch('')}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                            sx={{ width: 220 }}
                                        />
                                    </Box>

                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Search Term</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Count</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Share</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Dominant Outcome</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Matched Platform</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredWords
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((w, idx) => {
                                                        const cfg = OUTCOME_CONFIG[w.dominant_outcome] || OUTCOME_CONFIG.not_found;
                                                        return (
                                                            <TableRow
                                                                key={w.text}
                                                                hover
                                                                selected={selectedWord === w.text}
                                                                onClick={() => setSelectedWord(selectedWord === w.text ? null : w.text)}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <TableCell color="text.secondary">
                                                                    {page * rowsPerPage + idx + 1}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        {w.text}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" fontWeight={700}>
                                                                        {w.value.toLocaleString()}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {w.percentage}%
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        size="small"
                                                                        label={cfg.label}
                                                                        sx={{
                                                                            backgroundColor: cfg.bg,
                                                                            color: cfg.color,
                                                                            fontWeight: 600,
                                                                            fontSize: '0.68rem',
                                                                            height: 22,
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {w.matched_platform ? (
                                                                        <Chip
                                                                            size="small"
                                                                            label={w.matched_platform.toUpperCase()}
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            —
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                {filteredWords.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                No matching terms found.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        rowsPerPageOptions={[10, 15, 25, 50]}
                                        component="div"
                                        count={filteredWords.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Recent Inquiries Feed */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        Recent Query Activity
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                        Latest user searches from live traffic
                                    </Typography>

                                    <Stack spacing={1.2} sx={{ maxHeight: 440, overflowY: 'auto', pr: 0.5 }}>
                                        {data.recent_logs.map((log) => {
                                            const cfg = OUTCOME_CONFIG[log.outcome] || OUTCOME_CONFIG.not_found;
                                            const timeStr = new Date(log.created_at).toLocaleDateString('en-IN', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            });

                                            return (
                                                <Box
                                                    key={log.id}
                                                    sx={{
                                                        p: 1.2,
                                                        borderRadius: 1.5,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        backgroundColor: alpha(theme.palette.background.paper, 0.4),
                                                    }}
                                                >
                                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            "{log.raw_query}"
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={cfg.label.split(' ')[0]}
                                                            sx={{
                                                                backgroundColor: cfg.bg,
                                                                color: cfg.color,
                                                                fontSize: '0.65rem',
                                                                height: 20,
                                                                fontWeight: 700,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            User #{log.user_id} ({log.username})
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {timeStr}
                                                        </Typography>
                                                    </Box>
                                                    {log.matched_platform && (
                                                        <Box mt={0.5}>
                                                            <Chip
                                                                size="small"
                                                                label={`Mapped: ${log.matched_platform.toUpperCase()}`}
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ height: 18, fontSize: '0.62rem' }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        {data.recent_logs.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                                                No recent query logs.
                                            </Typography>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default RequestWordCloud;
